import { ImageIcon } from 'lucide-react';
import { cn } from '../../../lib/utils';

/**
 * Loaded assistant attachment / chart image sizing.
 * Preserves natural aspect ratio (no square crop) with a max width of 24rem.
 */
export const ASSISTANT_IMAGE_SIZE_CLASS = 'h-auto w-full max-w-96 min-w-0 object-contain';

/** Square placeholder while an assistant image is generating or loading. */
export const ASSISTANT_IMAGE_SKELETON_SIZE_CLASS = 'w-full max-w-96 min-w-0 aspect-square';

/** Wrapper for image preview / skeleton blocks inside chat message rows. */
export const ASSISTANT_IMAGE_WRAPPER_CLASS = 'w-full max-w-96 min-w-0';

/** Left offset aligned with assistant message text (desktop only). */
export const ASSISTANT_IMAGE_MARGIN_CLASS = 'sm:ml-4';

export const IMAGE_GENERATION_STARTED_MESSAGE = 'Image generation started. Please wait...';

export function isImageGenerationPendingContent(content?: unknown): boolean {
  if (typeof content !== 'string') return false;
  const trimmed = content.trim();
  return (
    trimmed === IMAGE_GENERATION_STARTED_MESSAGE ||
    trimmed.startsWith('Image generation started')
  );
}

interface ImageGenerationSkeletonProps {
  label?: string;
  className?: string;
}

export function ImageGenerationSkeleton({
  label = 'Creating image',
  className,
}: Readonly<ImageGenerationSkeletonProps>) {
  return (
    <div
      className={cn(
        'relative flex flex-col items-center justify-center gap-3 rounded-2xl',
        'border border-border/50 bg-muted/40',
        ASSISTANT_IMAGE_SKELETON_SIZE_CLASS,
        className
      )}
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-background/80 shadow-sm ring-1 ring-border/50">
        <ImageIcon className="h-5 w-5 text-primary/80" strokeWidth={1.75} aria-hidden />
      </div>

      <p className="text-sm text-muted-foreground">
        {label}
        <span className="ml-0.5 inline-flex" aria-hidden>
          <span className="loading-dot">.</span>
          <span className="loading-dot">.</span>
          <span className="loading-dot">.</span>
        </span>
      </p>
    </div>
  );
}
